import { useEffect, useRef, useState } from 'react';

import { DateRangePicker, Icon, Modal } from '@components';
import { DatePickerType } from '@constants/groups';
import {
  customDateRangeMinDaysDiff,
  customDefaultDateRange,
  DashboardDateRange,
  DashboardDateRangePickerTypes,
  DashboardDateRangePickerTypesEnum
} from '@features/dashboard/constants';
import { getSelectedDateRange } from '@features/dashboard/helper';
import { DateRange } from '@types';
import {
  formatISODate,
  getLastYearDate,
  getStartAndEndDateOfLastMonth,
  getStartAndPresentDateOfCurrentMonth
} from '@utils/date';

import theme from '../../../../../themes/colors';

interface DashboardDateRangePickerProps {
  minimumDateDifference?: number;
  minDate?: Date;
  handleDateRangeSelect: (
    data: DashboardDateRange,
    dateRangeType: DashboardDateRangePickerTypesEnum
  ) => void;
}

const DashboardDateRangePicker = ({
  minimumDateDifference,
  minDate,
  handleDateRangeSelect
}: DashboardDateRangePickerProps) => {
  const { startDate, endDate } = getStartAndPresentDateOfCurrentMonth();

  const [selectedDateRangeType, setSelectedDateRangeType] =
    useState<DashboardDateRangePickerTypesEnum>(
      DashboardDateRangePickerTypes[0].key
    );
  const [showCustomDateModal, setShowCustomDateModal] =
    useState<boolean>(false);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate: new Date(startDate),
    endDate: new Date(endDate)
  });
  const [underlineStyle, setUnderlineStyle] = useState({ left: 0, width: 0 });
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const selectedIndex = DashboardDateRangePickerTypes.findIndex(
      (item) => item.key === selectedDateRangeType
    );
    const selectedElement = optionRefs.current[selectedIndex];

    if (selectedElement) {
      const { offsetLeft, offsetWidth } = selectedElement;
      setUnderlineStyle({
        left: offsetLeft,
        width: offsetWidth
      });
    }
  }, [selectedDateRangeType]);

  const handleDateOptionClick = (
    dateRangeType: DashboardDateRangePickerTypesEnum
  ) => {
    if (dateRangeType === selectedDateRangeType) return;

    setSelectedDateRangeType(dateRangeType);
    if (dateRangeType === DashboardDateRangePickerTypesEnum.thisMonth) {
      setSelectedDateRange(getStartAndPresentDateOfCurrentMonth());
      handleDateRangeSelect(
        getSelectedDateRange(getStartAndPresentDateOfCurrentMonth()),
        dateRangeType
      );
    } else if (dateRangeType === DashboardDateRangePickerTypesEnum.lastMonth) {
      setSelectedDateRange(getStartAndEndDateOfLastMonth());
      handleDateRangeSelect(
        getSelectedDateRange(getStartAndEndDateOfLastMonth()),
        dateRangeType
      );
    } else if (dateRangeType === DashboardDateRangePickerTypesEnum.custom) {
      setSelectedDateRange(customDefaultDateRange);
      handleDateRangeSelect(
        getSelectedDateRange(customDefaultDateRange),
        dateRangeType
      );
    }
  };

  const displayStartDate = formatISODate({
    date: selectedDateRange.startDate,
    format: 'DD MMM'
  });

  const displayEndDate = formatISODate({
    date: selectedDateRange.endDate,
    format: 'DD MMM'
  });

  const onClickDisplayDateRange = () => {
    handleDateOptionClick(DashboardDateRangePickerTypesEnum.custom);
    setShowCustomDateModal(true);
  };

  const isCustomDateRange =
    selectedDateRangeType === DashboardDateRangePickerTypesEnum.custom;

  useEffect(() => {
    handleDateOptionClick(DashboardDateRangePickerTypesEnum.thisMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative flex h-11 flex-wrap gap-4 rounded-md bg-cofeeGreyBlue100 p-1 lg:flex-nowrap">
      <div className="relative flex w-fit items-center gap-0.5 p-0.5">
        {/* Sliding background */}
        <div
          className={`duration-250 absolute h-9 border border-cofeeNeutral4 bg-white shadow-sm transition-all ease-out ${
            isCustomDateRange ? 'rounded-l-md' : 'rounded-md'
          }`}
          style={{
            left: `${underlineStyle.left}px`,
            width: `${underlineStyle.width}px`
          }}
        />
        {DashboardDateRangePickerTypes.map((item, index) => (
          <div
            ref={(el) => {
              optionRefs.current[index] = el;
            }}
            className={`relative z-10 cursor-pointer rounded-md px-4 py-3 text-sm font-semibold ${
              selectedDateRangeType === item.key
                ? 'text-cofeePrimary7'
                : 'text-cofeeNeutral8'
            }`}
            key={item.key}
            onClick={() => handleDateOptionClick(item.key)}
            role="presentation"
          >
            {item.label}
          </div>
        ))}
        <div
          className={`-ml-1 flex h-9 w-fit cursor-pointer items-center gap-1.5 rounded-r-md border border-l-0 border-cofeeNeutral4 px-3 ${
            isCustomDateRange
              ? 'bg-white text-cofeeNeutral10'
              : 'bg-cofeeGreyBlue100 text-cofeeNeutral7'
          }`}
          onClick={onClickDisplayDateRange}
          role="presentation"
        >
          <Icon
            name="calendar-svg"
            stroke={
              isCustomDateRange ? theme.cofeeNeutral8 : theme.cofeeNeutral7
            }
          />
          <span className="text-sm font-medium">{`${displayStartDate} - ${displayEndDate}`}</span>
        </div>
      </div>
      {/* Todo: New calendar design in dashboard : wip */}
      {/* <div className="absolute right-0 top-0 z-50 bg-cofeeNeutral1">
        <DateRangePicker
          cancelButtonLabel="Cancel"
          confirmButtonLabel="Apply"
          handleConfirm={(dateRange) => {
            setShowCustomDateModal(false);
            setSelectedDateRange(dateRange);
            handleDateRangeSelect(
              getSelectedDateRange(dateRange),
              DashboardDateRangePickerTypesEnum.custom
            );
          }}
          handleCancel={() => setShowCustomDateModal(false)}
          defaultDatePickerType={DatePickerType.CUSTOM}
          showPicker={false}
          defaultDateRange={selectedDateRange}
          minimumDateDifference={
            minimumDateDifference || customDateRangeMinDaysDiff
          }
          minDate={getLastYearDate()}
          showTitle={false}
          showSelectedDate={false}
        />
      </div> */}

      {showCustomDateModal && (
        <Modal
          isOpen={showCustomDateModal}
          onCancel={() => setShowCustomDateModal(false)}
        >
          <DateRangePicker
            title="Select Date Range"
            cancelButtonLabel="Cancel"
            confirmButtonLabel="Apply"
            handleConfirm={(dateRange) => {
              setShowCustomDateModal(false);
              setSelectedDateRange(dateRange);
              handleDateRangeSelect(
                getSelectedDateRange(dateRange),
                DashboardDateRangePickerTypesEnum.custom
              );
            }}
            handleCancel={() => setShowCustomDateModal(false)}
            defaultDatePickerType={DatePickerType.CUSTOM}
            showPicker={false}
            defaultDateRange={selectedDateRange}
            minimumDateDifference={
              minimumDateDifference || customDateRangeMinDaysDiff
            }
            minDate={minDate ?? getLastYearDate()}
          />
        </Modal>
      )}
    </div>
  );
};

export default DashboardDateRangePicker;
